import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Alert, Badge, Button, Card, Modal, Nav, ProgressBar, Spinner, Table } from 'react-bootstrap';
import { ArrowClockwise, Send, Trash, Upload } from 'react-bootstrap-icons';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, reasonOf } from '@/components/tools/clientSelectionUtils';
import { ToolPage } from '@/components/ui/ToolPage';
import { activityService } from '@/services/activityService';
import { clientService } from '@/services/clientService';
import { supportDocsService, type MasterSupportDoc } from '@/services/supportDocsService';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { Client } from '@/types';
import './SupportDocsManager.css';

type DeployState = 'idle' | 'queued' | 'running' | 'success' | 'failed';

interface ClientDeployStatus {
  state: DeployState;
  message: string;
  progress: number;
}

const STATE_BADGE: Record<DeployState, { bg: string; label: string }> = {
  idle: { bg: 'secondary', label: 'Not started' },
  queued: { bg: 'secondary', label: 'Waiting' },
  running: { bg: 'info', label: 'Sending' },
  success: { bg: 'success', label: 'Done' },
  failed: { bg: 'danger', label: 'Failed' },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

export default function SupportDocsManager() {
  const [activeTab, setActiveTab] = useState('manage');
  const [masterDocs, setMasterDocs] = useState<MasterSupportDoc[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MasterSupportDoc | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployStatuses, setDeployStatuses] = useState<Record<string, ClientDeployStatus>>({});
  const [confirmSend, setConfirmSend] = useState(false);

  const selectedClients = useMemo(
    () => clients.filter((client) => selectedClientNames.includes(client.name)),
    [clients, selectedClientNames]
  );

  const loadMasterDocs = async () => {
    setLoadingDocs(true);
    setDocsError(null);
    try {
      const docs = await supportDocsService.listMasterDocs();
      setMasterDocs(docs);
    } catch (error) {
      setDocsError(`The master documents did not load. ${reasonOf(error)} Press Refresh to try again.`);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const allClients = await clientService.getAllClients();
      setClients(allClients);
    } catch (error) {
      setDeployError(`The client list did not load. ${reasonOf(error)} Reload the page to try again.`);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    loadMasterDocs();
    loadClients();
  }, []);

  const handleUploadFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    setUploading(true);
    setDocsError(null);
    try {
      for (const file of Array.from(files)) {
        await supportDocsService.uploadMasterDoc(file);
      }
      await loadMasterDocs();
    } catch (error) {
      setDocsError(
        `The upload did not finish. ${reasonOf(error)} Press Refresh to see which files were saved, then upload the rest again.`
      );
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDeleteDoc = async (key: string) => {
    setConfirmDelete(null);
    setDeletingKey(key);
    setDocsError(null);
    try {
      await supportDocsService.deleteMasterDoc(key);
      await loadMasterDocs();
    } catch (error) {
      setDocsError(`The document was not deleted. ${reasonOf(error)} Try again.`);
    } finally {
      setDeletingKey(null);
    }
  };

  const updateDeployStatus = (clientName: string, status: Partial<ClientDeployStatus>) => {
    setDeployStatuses((prev) => ({
      ...prev,
      [clientName]: {
        state: status.state ?? prev[clientName]?.state ?? 'idle',
        message: status.message ?? prev[clientName]?.message ?? '',
        progress: status.progress ?? prev[clientName]?.progress ?? 0,
      },
    }));
  };

  const deployToClients = async (targets: Client[]) => {
    setConfirmSend(false);
    setDeploying(true);
    setDeployError(null);

    const initialStatuses: Record<string, ClientDeployStatus> = {};
    for (const client of targets) {
      initialStatuses[client.name] = { state: 'queued', message: '', progress: 0 };
    }
    setDeployStatuses(initialStatuses);

    try {
      for (const client of targets) {
        updateDeployStatus(client.name, { state: 'running', message: 'Copying the documents', progress: 0 });

        try {
          const result = await supportDocsService.deployToClient(client.name);

          updateDeployStatus(client.name, {
            state: 'success',
            progress: 100,
            message: `${countOf(result.uploadedCount, 'document')} copied, ${countOf(result.cleanedCount, 'old file')} removed${
              result.kbCreated ? ', folder made' : ''
            }.`,
          });

          await activityService.logActivity({
            type: 'system',
            action: 'deployed',
            resourceType: 'support-docs',
            resourceId: client.name,
            details: {
              metadata: {
                uploadedCount: result.uploadedCount,
                cleanedCount: result.cleanedCount,
                kbCreated: result.kbCreated,
              },
            },
            success: true,
          });
        } catch (error) {
          const message = reasonOf(error);
          updateDeployStatus(client.name, {
            state: 'failed',
            progress: 100,
            message,
          });
          await activityService.logActivity({
            type: 'system',
            action: 'deployed',
            resourceType: 'support-docs',
            resourceId: client.name,
            details: {
              metadata: {
                error: message,
              },
            },
            success: false,
            errorMessage: message,
          });
        }
      }
    } catch (error) {
      setDeployError(`Nothing was sent. ${reasonOf(error)} Try again.`);
    } finally {
      setDeploying(false);
    }
  };

  const statusEntries = Object.entries(deployStatuses);
  const doneCount = statusEntries.filter(([, s]) => s.state === 'success').length;
  const failedCount = statusEntries.filter(([, s]) => s.state === 'failed').length;
  const noMasterDocs = !loadingDocs && masterDocs.length === 0;

  return (
    <ToolPage title="Support docs" subtitle="Edit the master support documents and send them to client workspaces.">
      <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => setActiveTab(tab || 'manage')} className="docs-tabs">
        <Nav.Item>
          <Nav.Link eventKey="manage">
            Master documents
            {!loadingDocs && <span className="docs-tab-count">{masterDocs.length}</span>}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="deploy">Send to clients</Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === 'manage' && (
        <>
          <div className="docs-toolbar">
            <p className="docs-toolbar__note">
              This is the master set. Sending puts exactly these files in a client&apos;s Numa Support folder. A file
              with the same name as one here replaces it.
            </p>
            <div className="docs-toolbar__actions">
              <Button variant="outline-secondary" onClick={loadMasterDocs} disabled={loadingDocs || uploading}>
                {loadingDocs ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <ArrowClockwise className="me-1" aria-hidden="true" />
                )}
                Refresh
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.doc,.docx,.txt,.md,.html,.htm"
                onChange={handleUploadFiles}
              />
              <Button variant="primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <Upload className="me-1" aria-hidden="true" />
                )}
                {uploading ? 'Uploading' : 'Upload documents'}
              </Button>
            </div>
          </div>

          {docsError && <Alert variant="danger">{docsError}</Alert>}

          <Card>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>File</th>
                  <th className="text-end">Size</th>
                  <th>Uploaded by</th>
                  <th>Changed</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {loadingDocs && (
                  <tr>
                    <td colSpan={5} className="docs-empty">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading the master documents
                    </td>
                  </tr>
                )}
                {!loadingDocs && masterDocs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="docs-empty">
                      {docsError
                        ? 'Nothing to show until the documents load.'
                        : 'No master documents yet. Upload a PDF, Word, text, Markdown or HTML file to start.'}
                    </td>
                  </tr>
                )}
                {!loadingDocs &&
                  masterDocs.map((doc) => (
                    <tr key={doc.key}>
                      <td className="docs-file">{doc.key}</td>
                      <td className="text-end numeric">{formatBytes(doc.size)}</td>
                      <td>{doc.uploadedBy}</td>
                      <td>
                        {doc.lastModified && (
                          <>
                            <div>{timeAgo(doc.lastModified)}</div>
                            <div className="docs-sub">{formatNzDateTime(doc.lastModified)}</div>
                          </>
                        )}
                      </td>
                      <td className="docs-actions">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setConfirmDelete(doc)}
                          disabled={deletingKey === doc.key}
                        >
                          <Trash size={12} className="me-1" />
                          {deletingKey === doc.key ? 'Deleting' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {activeTab === 'deploy' && (
        <>
          {deployError && <Alert variant="danger">{deployError}</Alert>}

          <div className="docs-send">
            <Card>
              <Card.Body>
                <h2 className="section-label">Clients</h2>
                <GroupedClientSelector
                  clients={clients}
                  selectedClientNames={selectedClientNames}
                  onClientToggle={(name) =>
                    setSelectedClientNames((prev) =>
                      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
                    )
                  }
                  onSelectClients={setSelectedClientNames}
                  disabled={deploying}
                  loading={loadingClients}
                />
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <h2 className="section-label">What sending does</h2>
                <div className="tool-effect docs-send__what">
                  <ul>
                    <li>
                      Copies{' '}
                      {loadingDocs ? 'the master documents' : `the ${countOf(masterDocs.length, 'master document')}`}{' '}
                      into the Numa Support folder of each chosen client. A file already there with the same name is
                      replaced.
                    </li>
                    <li>Removes any file in that folder that is not in the master set.</li>
                    <li>
                      Makes the folder on a client that does not have it yet. Everyone on the workspace can read it.
                    </li>
                  </ul>
                </div>
                {noMasterDocs && (
                  <Alert variant="danger">
                    There are no master documents. Sending now empties the Numa Support folder on each chosen client.
                  </Alert>
                )}
                <div className="docs-send__go">
                  <Button
                    variant="primary"
                    onClick={() => setConfirmSend(true)}
                    disabled={deploying || selectedClients.length === 0}
                  >
                    {deploying ? (
                      <Spinner size="sm" animation="border" className="me-2" />
                    ) : (
                      <Send className="me-2" aria-hidden="true" />
                    )}
                    {deploying ? 'Sending' : `Send to ${countOf(selectedClients.length, 'client')}`}
                  </Button>
                  <span className="docs-send__hint">
                    {selectedClients.length === 0
                      ? 'Pick at least one client. Tick "All clients" to send to every one.'
                      : 'Clients are done one after the other.'}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </div>

          {statusEntries.length > 0 && (
            <>
              <h2 className="section-label docs-results-label">Result by client</h2>
              <p className="docs-results-note">
                {doneCount} of {countOf(statusEntries.length, 'client')} done
                {failedCount > 0 ? `, ${failedCount} failed` : ''}
                {deploying ? '. Still sending.' : '.'}
              </p>
              <Card>
                <div className="docs-results">
                  <Table hover className="mb-0 docs-results__table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>State</th>
                        <th>Progress</th>
                        <th>What happened</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusEntries.map(([clientName, status]) => (
                        <tr key={clientName}>
                          <td className="docs-file">{clientName}</td>
                          <td>
                            <Badge bg={STATE_BADGE[status.state].bg}>{STATE_BADGE[status.state].label}</Badge>
                          </td>
                          <td className="docs-results__bar">
                            {/* One call copies a client's whole set, so a running client shows movement, not a count. */}
                            <ProgressBar
                              now={status.state === 'running' ? 100 : status.progress}
                              animated={status.state === 'running'}
                              striped={status.state === 'running'}
                              variant={status.state === 'failed' ? 'danger' : undefined}
                              aria-label={status.state === 'running' ? 'Sending' : `${status.progress}% sent`}
                            />
                          </td>
                          <td className={status.state === 'failed' ? 'docs-results__reason--bad' : undefined}>
                            {status.message}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      <Modal show={confirmDelete !== null} onHide={() => setConfirmDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete master document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <span className="mono">{confirmDelete?.key}</span> leaves the master set. No client changes now.
          </p>
          <p className="mb-0">The next time you send to a client, the file is removed from its Numa Support folder.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => confirmDelete && void handleDeleteDoc(confirmDelete.key)}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmSend} onHide={() => setConfirmSend(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send support docs to {countOf(selectedClients.length, 'client')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noMasterDocs ? (
            <Alert variant="danger">
              There are no master documents. This empties the Numa Support folder on each of these clients.
            </Alert>
          ) : (
            <p>
              Each client&apos;s Numa Support folder ends up holding the {countOf(masterDocs.length, 'master document')}{' '}
              and nothing else.
            </p>
          )}
          <div className="docs-confirm-list">
            {selectedClients.map((client) => (
              <div key={client.name} className="mono">
                {client.name}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmSend(false)}>
            Cancel
          </Button>
          <Button variant={noMasterDocs ? 'danger' : 'primary'} onClick={() => void deployToClients(selectedClients)}>
            Send to {countOf(selectedClients.length, 'client')}
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
